import { React, useRef, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useIdentity } from '@zenbusiness/application-commons-identity';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { useTheme } from '@zenbusiness/zui/styles';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import AnonymousBanner from '#client/components/AnonymousBanner';
import AppHeader from '#client/components/AppHeader';
import AuthModalManager from '#client/components/AuthModal';
import ProfileMenu from '#client/components/ProfileMenu';
import { THREAD_MODES, CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';
import AuthActionContext from '#client/contexts/AuthActionContext';
import { ThreadListProvider, useThreadListContext } from '#client/contexts/ThreadListContext';
import { useUnifiedBusinessEntityContext } from '#client/contexts/UnifiedBusinessEntityProvider';
import {
  useSidebarState,
  useThreadNavigation,
  useClickOutside,
  useAuthModal,
  useUserMode
} from '#client/hooks';
import { useCloseNavigation } from '#client/hooks/useCloseNavigation';
import { useNavigationContext } from '#client/hooks/useNavigationContext';

import ThreadSidebar from '../ThreadSidebar';
import * as Styled from './AssistantLayout.style';
/**
 * Main layout wrapper for Assistant pages that provides sidebar functionality and header
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - The main content to be rendered (ThreadCreator or ThreadViewer)
 * @param {boolean} props.disableSidebar - Whether to disable the sidebar
 * @param {boolean} props.disableThreadHistory - Whether to disable the thread history
 * @param {string} props.threadMode - Explicit mode from thread context ('anonymous' | 'starter' | 'business' | 'standalone')
 * @returns {React.ReactElement} The AssistantLayout component
 */
function AssistantLayoutInner({ children, disableSidebar, disableThreadHistory, threadMode }) {
  const { isVisible: sidebarVisible, toggleSidebar, hideSidebar } = useSidebarState();
  const { threads, loading: threadsLoading, refetch: refetchThreads } = useThreadListContext(); // Use centralized context
  const { currentThreadUuid, navigateToThread, navigateToNewThread } = useThreadNavigation();
  const enableThreadHistory = useExperimentFlag('enable-velo-thread-history', false);
  const enableNewChat = useExperimentFlag('enable-velo-new-chat', false);
  const enableMultiVeloMode = useExperimentFlag('enable-multi-velo-mode', false);
  const enableVeloStarterMode = useExperimentFlag('enable-velo-starter-mode', false);

  const lastSidebarRefetchTime = useRef(0);
  const { businessEntityUuid, loading: entityLoading } = useUnifiedBusinessEntityContext();

  // Mode and authentication management
  const { authenticated } = useAuthentication();
  const identity = useIdentity();
  const authModal = useAuthModal();
  const userMode = useUserMode(threadMode);

  // Refs for click-outside detection
  const sidebarRef = useRef(null);
  const headerRef = useRef(null);

  // Use Material-UI breakpoints for responsive behavior
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Initialize navigation context tracking (from AppContainer)
  useNavigationContext();

  // Get close navigation handler (from AppContainer)
  const { handleCloseChat } = useCloseNavigation();

  const trackButtonPressed = useClickstreamCallback('buttonPressed');

  // Initialize error logging
  const captureException = useCaptureException({
    tags: {
      frontend: 'assistant-ui',
      operationName: 'assistantLayout'
    }
  });

  // Click outside handler - close on desktop, stay open on mobile
  const handleClickOutside = useCallback(() => {
    if (!isMobileScreen) {
      hideSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileScreen, hideSidebar, sidebarVisible]);

  // Determine if click-outside should be enabled
  // Enable when sidebar is visible (since it's absolutely positioned and overlays content)
  // But only close the sidebar on smaller screens where it truly overlays
  const shouldEnableClickOutside = sidebarVisible;

  // Handle click outside sidebar, excluding the header (which contains the toggle button)
  useClickOutside(sidebarRef, handleClickOutside, shouldEnableClickOutside, [headerRef]);

  const handleViewHistory = useCallback(() => {
    // Anonymous users: Show upgrade modal instead of history (only if multi-mode is enabled)
    if (enableMultiVeloMode && userMode.isAnonymous) {
      trackButtonPressed({
        ...CLICKSTREAM_COMMON_PROPS,
        elementName: 'view_history_blocked',
        elementText: 'View history (anonymous - upgrade required)'
      });
      authModal.openUpgrade();
      return;
    }

    trackButtonPressed({
      ...CLICKSTREAM_COMMON_PROPS,
      elementName: 'view_history',
      elementText: `View history ${sidebarVisible ? 'closed' : 'open'}`
    });
    toggleSidebar();

    if (!sidebarVisible) {
      const now = Date.now();
      if (now - lastSidebarRefetchTime.current > 10000) {
        refetchThreads();
        lastSidebarRefetchTime.current = now;
      }
    }
  }, [
    toggleSidebar,
    trackButtonPressed,
    sidebarVisible,
    refetchThreads,
    enableMultiVeloMode,
    userMode.isAnonymous,
    authModal
  ]);

  const handleNewChat = useCallback(() => {
    // Anonymous users: Show upgrade modal instead of creating new chat (only if multi-mode is enabled)
    if (enableMultiVeloMode && userMode.isAnonymous) {
      trackButtonPressed({
        ...CLICKSTREAM_COMMON_PROPS,
        elementName: 'new_chat_blocked',
        elementText: 'New chat (anonymous - upgrade required)'
      });
      authModal.openUpgrade();
      return;
    }

    // If business entity is still loading, don't proceed
    trackButtonPressed({
      ...CLICKSTREAM_COMMON_PROPS,
      elementName: 'new_chat',
      elementText: 'New chat'
    });

    if (entityLoading) {
      captureException(new Error('Cannot start new chat: business entity is still loading'), {
        tags: {
          errorType: 'business_entity_loading',
          userAction: 'new_chat_attempt'
        },
        contexts: {
          businessEntity: {
            loading: entityLoading,
            uuid: businessEntityUuid || null,
            hasUuid: !!businessEntityUuid
          },
          userAction: {
            action: 'newChatAttempt',
            timestamp: new Date().toISOString(),
            blocked: true,
            reason: 'entity_loading'
          }
        }
      });
      return;
    }

    // If no business entity UUID is available, log error and don't proceed
    if (!businessEntityUuid) {
      captureException(new Error('Cannot start new chat: businessEntityUuid is not available'), {
        tags: {
          errorType: 'missing_business_entity_uuid',
          userAction: 'new_chat_attempt'
        },
        contexts: {
          businessEntity: {
            loading: entityLoading,
            uuid: businessEntityUuid || null,
            hasUuid: !!businessEntityUuid
          },
          userAction: {
            action: 'newChatAttempt',
            timestamp: new Date().toISOString(),
            blocked: true,
            reason: 'missing_uuid'
          }
        }
      });
      return;
    }

    navigateToNewThread(businessEntityUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    businessEntityUuid,
    entityLoading,
    navigateToNewThread,
    trackButtonPressed,
    enableMultiVeloMode,
    userMode.isAnonymous,
    authModal
  ]);

  const handleThreadSelect = (threadUuid) => {
    navigateToThread(threadUuid);
  };

  // Memoize auth actions for context (avoids re-renders)
  const authActions = useMemo(
    () => ({
      openCreateAccount: authModal.openCreateAccount,
      openLogin: authModal.openLogin
    }),
    [authModal.openCreateAccount, authModal.openLogin]
  );

  // TODO: TEMP_ANONYMOUS_HIDE - Remove this block when anonymous mode UI restrictions are no longer needed
  const shouldShowForAnonymous =
    (enableMultiVeloMode && enableVeloStarterMode) || userMode.isBusiness;
  return (
    <Styled.Site>
      {/* AuthModalManager - Top level for z-index */}
      <AuthModalManager {...authModal} />

      <Styled.Page>
        <Styled.Header ref={headerRef}>
          <AppHeader
            profileMenuSlot={
              shouldShowForAnonymous ? (
                <ProfileMenu
                  isAuthenticated={authenticated}
                  threadMode={userMode.mode}
                  user={identity.user}
                  onLogin={authModal.openLogin}
                />
              ) : null
            }
            sideMenuOpen={enableThreadHistory && sidebarVisible && !disableSidebar}
            title="Velo™"
            onClose={handleCloseChat}
            onHistory={
              enableThreadHistory && !disableThreadHistory && shouldShowForAnonymous
                ? handleViewHistory
                : null
            }
            onNewChat={enableNewChat && shouldShowForAnonymous ? handleNewChat : null}
          />
        </Styled.Header>

        <AuthActionContext.Provider value={authActions}>
          <Styled.MainWrapper>
            {/* AnonymousBanner - Shown only in anonymous mode when multi-mode is enabled */}
            {userMode.isAnonymous && shouldShowForAnonymous && (
              <Styled.BannerContainer>
                <AnonymousBanner dismissible onCreateAccount={authModal.openCreateAccount} />
              </Styled.BannerContainer>
            )}

            <Styled.LayoutContainer>
              <Styled.SidebarContainer
                ref={sidebarRef}
                data-testid="assistant-sidebar"
                isVisible={enableThreadHistory && sidebarVisible && !disableSidebar}
              >
                <ThreadSidebar
                  currentThreadUuid={currentThreadUuid}
                  isVisible={enableThreadHistory && sidebarVisible && !disableThreadHistory}
                  loading={threadsLoading}
                  threads={threads}
                  onThreadSelect={handleThreadSelect}
                  onToggleSidebar={toggleSidebar}
                />
              </Styled.SidebarContainer>

              <Styled.MainContentContainer
                data-testid="assistant-main-content"
                sidebarVisible={sidebarVisible}
              >
                <Styled.ContentWrapper>{children}</Styled.ContentWrapper>
              </Styled.MainContentContainer>
            </Styled.LayoutContainer>
          </Styled.MainWrapper>
        </AuthActionContext.Provider>
      </Styled.Page>
    </Styled.Site>
  );
}

AssistantLayoutInner.propTypes = {
  children: PropTypes.node.isRequired,
  disableSidebar: PropTypes.bool,
  disableThreadHistory: PropTypes.bool,
  threadMode: PropTypes.oneOf(Object.values(THREAD_MODES))
};

/**
 * Main layout wrapper for Assistant pages that provides sidebar functionality and header
 * Now wrapped with ThreadsProvider to centralize thread state management
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - The main content to be rendered (ThreadCreator or ThreadViewer)
 * @param {boolean} props.disableSidebar - Whether to disable the sidebar
 * @param {boolean} props.disableThreadHistory - Whether to disable the thread history
 * @param {string} props.threadMode - Explicit mode from thread context ('anonymous' | 'starter' | 'business' | 'standalone')
 * @returns {React.ReactElement} The AssistantLayout component
 */
function AssistantLayout({ children, disableSidebar, disableThreadHistory, threadMode }) {
  return (
    <ThreadListProvider>
      <AssistantLayoutInner
        disableSidebar={disableSidebar}
        disableThreadHistory={disableThreadHistory}
        threadMode={threadMode}
      >
        {children}
      </AssistantLayoutInner>
    </ThreadListProvider>
  );
}

AssistantLayout.propTypes = {
  children: PropTypes.node.isRequired,
  disableSidebar: PropTypes.bool,
  disableThreadHistory: PropTypes.bool,
  threadMode: PropTypes.oneOf(Object.values(THREAD_MODES))
};

export default AssistantLayout;
